/**
 * NOLAS 2026, (c) Mikael FOURNY.
 * src/i18n/fr.contenu.ts
 *
 * Sous-catalogue "contenu et honnetete de l'interface" : etats vides,
 * actions de sortie, messages d'erreur explicites, et ecrans dont le
 * contenu etait factice ou appartenait a un autre evenement.
 *
 * Pourquoi un sous-catalogue plutot que `fr.ts` :
 *   `fr.ts` est LE point de contact de tous les lots de travail. Au lot
 *   precedent il avait ete gele, et chaque intervenant avait du SIGNALER
 *   ses cles au lieu de les ecrire : 47 cles a reporter a la main apres la
 *   fusion. Cette fois chaque lot a son fichier, il y ecrit librement, et
 *   `fr.ts` ne bouge plus.
 *
 * Ce fichier est fusionne APRES le noyau dans `fr.ts` : une cle redefinie
 * ici ECRASE celle du noyau. C'est le mecanisme qui fait disparaitre le
 * contenu herite de l'autre evenement sans toucher au fichier gele.
 * `__tests__/fr.contenu.test.ts` le PROUVE au lieu de le supposer.
 *
 * Regle de vocabulaire, non negociable : aucune valeur ne doit contenir en
 * dur le nom de l'evenement, une date, un lieu, ni les mots pilotes par le
 * lexique configurable. On passe par les jetons {eventType*} / {event*}
 * (identite) et {lexWorkshop*} / {lexExhibitor*} / {lexVisitor*} (lexique).
 *
 * Mikael - chef de projet - 2026-09-09
 */
export const messagesContenuFr: Record<string, string> = {
  // ========================================================================
  // 0. CE FICHIER NE REDEFINIT AUCUNE CLE DU NOYAU
  //
  // Une cle, une source. Redefinir ici une cle deja presente dans `fr.ts`
  // ferait gagner silencieusement la valeur d'ici, et la personne suivante
  // qui lirait `fr.ts` verrait un texte qui n'est pas celui affiche. Le test
  // `fr.contenu.test.ts` verifie cette regle plutot que de compter dessus.
  //
  // Consequence pour l'audit du 2026-09-09, point 3.6 : les deux chaines qui
  // decrivent l'evenement avec le vocabulaire d'un AUTRE salon
  // (`about.section.salon.p1` et `onboarding.walk1.body`) ne sont PAS
  // corrigees ici. Elles vivent dans `fr.ts`, qui est gele pour ce lot : le
  // texte de remplacement est propose dans le rapport, et sera pose dans
  // `fr.ts` par le chef de projet apres la fusion. Le present fichier ne
  // contient donc QUE des cles nouvelles.
  // ========================================================================

  // ========================================================================
  // 2. MON ESPACE : bandeau "pendant l'evenement" (audit 3.15)
  //
  // Le bandeau annoncait une valeur FACTICE codee en dur, jamais nulle, si
  // bien que la branche honnete etait du code mort. Il lit desormais le seul
  // endpoint reel qui existe (programme personnel du compte connecte) ; il
  // lui faut donc les deux etats qu'un vrai chargement peut produire.
  // ========================================================================
  'monEspace.phaseBanner.during.loading':
    'Recherche de votre prochain{lexWorkshopAgr} {lexWorkshopBare}…',
  'monEspace.phaseBanner.during.error':
    'Votre programme personnel n’a pas pu être chargé : le serveur est injoignable. Vérifiez votre connexion, puis réessayez.',
  'monEspace.phaseBanner.during.retry': 'Réessayer',
  // Variante SANS salle. Defaut trouve en chemin, absent de l'audit : les
  // cinq activites enregistrees en production au 2026-09-09 ont toutes une
  // salle vide, et le message unique "... : {title} ({room})" aurait affiche
  // une parenthese vide. On ne comble pas ce vide par une salle inventee.
  'monEspace.phaseBanner.during.nextWorkshopNoRoom':
    'Prochain{lexWorkshopAgr} {lexWorkshopBare} : {title}',

  // ========================================================================
  // 3. ESPACE PRESSE : un etat vide sans action de sortie est une impasse
  // ========================================================================
  'press.kit.notReady.help':
    'Le kit n’est pas encore en ligne. Écrivez au contact presse : il vous sera envoyé dès sa publication.',
  'press.resources.empty.help':
    'Les visuels ne sont pas encore déposés. Le contact presse peut vous les transmettre directement.',
  'press.releases.empty.help':
    'Aucun communiqué n’a encore été publié. Le contact presse vous préviendra de la prochaine parution.',
  'press.contact.jumpCta': 'Écrire au contact presse',

  // ========================================================================
  // 4. ANNUAIRE : compteur honnete, etat vide avec sortie, erreur avec issue
  //
  // Le compteur affichait un nombre qui changeait quand on filtrait par
  // secteur (30 vers 3) sans dire que c'etait le filtre. Quand un filtre est
  // actif, on dit "sur combien", ET on offre le bouton qui l'annule.
  // ========================================================================
  'exposants.stats.filteredNotice': 'Chiffres filtrés',
  'exposants.filters.reset': 'Réinitialiser les filtres',
  'exposants.empty.filtered':
    '{lexExhibitorNoneCap} ne correspond à votre recherche ou au secteur choisi.',
  'exposants.empty.none':
    'La liste {lexExhibitorOfPlural} n’est pas encore publiée. Elle se remplira au fur et à mesure des confirmations.',
  'exposants.error.title': 'Liste {lexExhibitorOfPlural} indisponible',
  'exposants.error.body':
    'La liste n’a pas pu être chargée : le serveur est injoignable. Vérifiez votre connexion, puis réessayez.',
  'exposants.error.retry': 'Réessayer',

  // ========================================================================
  // 5. INSCRIPTION A UNE ACTIVITE : le message le plus sensible de l'audit
  //
  // "Impossible de finaliser votre inscription." ne disait NI la cause NI ce
  // qu'il fallait faire, et laissait craindre une place perdue. On separe
  // les deux causes que le client sait reellement distinguer (serveur
  // injoignable, reponse d'erreur) et on affirme dans les deux cas ce qui
  // est VRAI : rien n'a ete enregistre.
  // ========================================================================
  'atelier.signup.error.network':
    'Inscription non enregistrée : le serveur est injoignable. Vérifiez votre connexion, puis réessayez, aucune place n’a été perdue.',
  'atelier.signup.error.server':
    'Inscription non enregistrée : le serveur a refusé la demande. Réessayez dans un instant ; si cela recommence, signalez-le depuis la page Contact.',
  'atelier.cancel.error.network':
    'Annulation non enregistrée : le serveur est injoignable. Vérifiez votre connexion, puis réessayez, votre inscription reste active.',
  // Defaut trouve en chemin : l'echec d'ajout ou de retrait d'un FAVORI
  // affichait le message d'echec d'INSCRIPTION, faisant croire a l'echec
  // d'une inscription que personne n'avait demandee.
  'atelier.favorite.error':
    'Favori non enregistré : le serveur n’a pas répondu. Vérifiez votre connexion, puis réessayez.',
  'atelier.cancel.error.server':
    'Annulation non enregistrée : le serveur a refusé la demande. Réessayez dans un instant ; si cela recommence, signalez-le depuis la page Contact.',

  // ========================================================================
  // 6. PAGES EDITORIALES : "Page introuvable" etait une impasse (audit 3.14)
  //
  // Les trois pages legales EXISTENT en base mais ne sont pas publiees : le
  // lecteur tombait sur un ecran sans aucune action. On ne pretend pas
  // connaitre la cause exacte (slug absent OU brouillon), on enonce les
  // deux, et on donne une sortie.
  // ========================================================================
  'cms.page.notFound.help':
    'Cette page n’est pas disponible : soit elle n’existe pas, soit elle n’a pas encore été publiée par l’organisation.',
  'cms.page.notFound.ctaContact': 'Nous contacter',
  'cms.page.notFound.ctaHome': 'Retour à l’accueil',
  'cms.page.error.help':
    'Le contenu n’a pas pu être chargé : le serveur est injoignable. Vérifiez votre connexion, puis réessayez.',
  'cms.page.error.retry': 'Réessayer',
};
